// Single source of truth for asset performance KPIs — drives the New-Asset grid
// columns, the asset-page Performance card, and (later) the Excel import mapper.
//
// Storage: all KPIs live in asset.engagement_metrics (a flat dict). Values are
// stored as entered (percent = percent number, ₹ = number, ROAS = x) EXCEPT
// `er_rate`, which stays a fraction (0.018 = 1.8%) for backward-compat with the
// reel-scoring objective — flagged `frac` so the UI converts on the way in/out.
//
// `aliases` are the column headers Meta Ads Manager / IG professional dashboard
// export, so a future "upload Excel" flow can auto-map them to our keys.

export const KPI_GROUPS = [
  { key: "creative", label: "Creative" },
  { key: "organic", label: "Organic · IG" },
  { key: "paid", label: "Paid · Meta Ads" },
];

export const KPI_FIELDS = [
  // ── Creative (cross-cutting — these feed the craft/objective score) ──
  { key: "hook_rate", label: "Hook %", group: "creative", unit: "pct", aliases: ["hook rate", "3-second video plays rate"] },
  { key: "hold_rate", label: "Hold / %Viewed", group: "creative", unit: "pct", aliases: ["hold rate", "thruplay rate", "% watched", "video average play time"] },
  { key: "avg_watch_time", label: "Avg watch (s)", group: "creative", unit: "sec", aliases: ["average watch time", "avg. watch time"] },
  { key: "er_rate", label: "Engagement %", group: "creative", unit: "pct", frac: true, aliases: ["engagement rate", "er"] },

  // ── Organic (Instagram Insights) ──
  { key: "views", label: "Views / Plays", group: "organic", unit: "num", aliases: ["views", "plays", "reel plays"] },
  { key: "reach_organic", label: "Reach", group: "organic", unit: "num", aliases: ["reach", "accounts reached"] },
  { key: "nonfollower_pct", label: "Non-follower %", group: "organic", unit: "pct", aliases: ["non-followers", "% non-followers"] },
  { key: "likes", label: "Likes", group: "organic", unit: "num", aliases: ["likes"] },
  { key: "comments", label: "Comments", group: "organic", unit: "num", aliases: ["comments"] },
  { key: "shares", label: "Shares", group: "organic", unit: "num", aliases: ["shares"] },
  { key: "saves", label: "Saves", group: "organic", unit: "num", aliases: ["saves", "saved"] },
  { key: "follows", label: "Follows", group: "organic", unit: "num", aliases: ["follows", "follows from post"] },
  { key: "profile_visits", label: "Profile visits", group: "organic", unit: "num", aliases: ["profile visits", "profile activity"] },

  // ── Paid (Meta Ads Manager) ──
  { key: "spend", label: "Spend ₹", group: "paid", unit: "inr", aliases: ["amount spent", "amount spent (inr)", "spend"] },
  { key: "impressions", label: "Impressions", group: "paid", unit: "num", aliases: ["impressions"] },
  { key: "paid_reach", label: "Paid reach", group: "paid", unit: "num", aliases: ["reach"] },
  { key: "frequency", label: "Frequency", group: "paid", unit: "num", aliases: ["frequency"] },
  { key: "cpm", label: "CPM ₹", group: "paid", unit: "inr", aliases: ["cpm", "cpm (cost per 1,000 impressions)"] },
  { key: "cpc", label: "CPC ₹", group: "paid", unit: "inr", aliases: ["cpc (cost per link click)", "cpc"] },
  { key: "ctr", label: "Link CTR %", group: "paid", unit: "pct", aliases: ["ctr (link click-through rate)", "ctr (all)", "ctr"] },
  { key: "link_clicks", label: "Link clicks", group: "paid", unit: "num", aliases: ["link clicks", "clicks (all)"] },
  { key: "purchases", label: "Purchases / Results", group: "paid", unit: "num", aliases: ["purchases", "results"] },
  { key: "roas", label: "ROAS", group: "paid", unit: "x", aliases: ["purchase roas", "website purchase roas", "roas"] },
  { key: "cpa", label: "Cost / result ₹", group: "paid", unit: "inr", aliases: ["cost per result", "cost per purchase", "cpa"] },
  { key: "cvr", label: "Conv. rate %", group: "paid", unit: "pct", aliases: ["conversion rate", "cvr"] },
  { key: "atc", label: "Add to cart", group: "paid", unit: "num", aliases: ["adds to cart", "website adds to cart"] },
];

export const KPI_KEYS = KPI_FIELDS.map((f) => f.key);
export const KPI_BY_KEY = Object.fromEntries(KPI_FIELDS.map((f) => [f.key, f]));

// number-in (UI value → stored value): er_rate stores as a fraction.
export function kpiToStore(field, raw) {
  if (raw === "" || raw == null) return undefined;
  const n = Number(String(raw).replace(/[, ₹×x%]/g, ""));
  if (Number.isNaN(n)) return undefined;
  return field.frac ? n / 100 : n;
}

// stored value → display string.
export function kpiDisplay(field, val) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  const v = field.frac ? n * 100 : n;
  if (field.unit === "pct") return `${round(v)}%`;
  if (field.unit === "inr") return `₹${v.toLocaleString("en-IN")}`;
  if (field.unit === "x") return `${round(v)}×`;
  if (field.unit === "sec") return `${round(v)}s`;
  return v.toLocaleString("en-IN");
}

// stored value → raw editable string (percent shown as percent).
export function kpiToInput(field, val) {
  if (val == null || val === "") return "";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return String(field.frac ? n * 100 : n);
}

function round(n) { return Math.round(n * 100) / 100; }
